import React, { useId } from "react"
import { cn } from "../utils"
import {
  OverflowScrollButtons,
  overflowScrollContainerStyles,
  overflowScrollStyles,
  useOverflowScroll
} from "../overflow-scroll"
import { VisuallyHidden } from "../visually-hidden"
import { PageSecondaryHeaderCenterContent } from "./page-secondary-header-center-content"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderStepsProps = {
  /** Page header steps */
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderStepsNavStyles = [
  ...overflowScrollStyles,
  // Room for the steps' outset focus ring, which the scroller would clip
  "py-1"
]

const pageSecondaryHeaderStepsStyles = ["flex items-center w-max shrink-0"]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header steps.
 *
 * Displays the current step in a workflow. The steps scroll with fades and
 * scroll buttons when they overflow, and the current step is scrolled into
 * view.
 */
export function PageSecondaryHeaderSteps({
  children,
  className
}: PageSecondaryHeaderStepsProps) {
  const accessibleNameId = useId()
  const { scrollerProps, scrollButtonsProps } = useOverflowScroll<HTMLElement>({
    activeAttribute: "aria-current"
  })

  return (
    <PageSecondaryHeaderCenterContent>
      <div className={cn(overflowScrollContainerStyles)}>
        <nav
          {...scrollerProps}
          aria-labelledby={accessibleNameId}
          className={cn(pageSecondaryHeaderStepsNavStyles)}
          data-slot="secondary-steps"
        >
          <VisuallyHidden id={accessibleNameId}>Steps</VisuallyHidden>
          <ol className={cn(pageSecondaryHeaderStepsStyles, className)}>
            {children}
          </ol>
        </nav>
        <OverflowScrollButtons
          {...scrollButtonsProps}
          data-slot="secondary-steps-scroll-button"
        />
      </div>
    </PageSecondaryHeaderCenterContent>
  )
}
